import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
export const BASE_URL = API_URL.replace('/api', '');

const api = axios.create({
    baseURL: API_URL,
    timeout: 35000, // Extended for IA analysis (Groq can take up to 20s)
});

// Request interceptor - add token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        if (!config.headers) config.headers = {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Response interceptor - handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const originalRequest = error.config;
            const isAuthRoute = originalRequest?.url.includes('/auth/login') || 
                               originalRequest?.url.includes('/auth/verify-2fa') ||
                               originalRequest?.url.includes('/auth/resend-2fa');
            
            // Do not force reload/logout if the error comes from an authentication endpoint
            if (!isAuthRoute) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
            }
        }
        return Promise.reject(error);
    }
);

// ─── Auth ──────────────────────────────────────────────────
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    verify2FA: (userId, code) => api.post('/auth/verify-2fa', { userId, code }),
    resend2FA: (userId) => api.post('/auth/resend-2fa', { userId }),
    getMe: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
    changePassword: (data) => api.put('/auth/change-password', data),
    getRecaptchaConfig: () => api.get('/auth/recaptcha-config'),
};

// ─── Dashboard ─────────────────────────────────────────────
export const dashboardAPI = {
    get: () => api.get('/dashboard'),
};

// ─── Sinistres ─────────────────────────────────────────────
export const sinistresAPI = {
    list: (params) => api.get('/sinistres', { params }),
    getById: (id) => api.get(`/sinistres/${id}`),
    create: (data) => api.post('/sinistres', data),
    update: (id, data) => api.put(`/sinistres/${id}`, data),
    delete: (id) => api.delete(`/sinistres/${id}`),
    searchClients: (query) => api.get('/sinistres/search-clients', { params: { query } }),
    getClientVehicles: (name) => api.get('/sinistres/client-vehicles', { params: { name } }),
    getNextNumber: () => api.get('/sinistres/next-number'),
};

// ─── Rapports ──────────────────────────────────────────────
export const rapportsAPI = {
    list: (params) => api.get('/rapports', { params }),
    getById: (id) => api.get(`/rapports/${id}`),
    create: (data) => api.post('/rapports', data),
    update: (id, data) => api.put(`/rapports/${id}`, data),
    generatePDF: (id) => api.post(`/rapports/${id}/pdf`, {}, { responseType: 'blob', timeout: 60000 }),
    analyseIA: (id) => api.post(`/rapports/${id}/analyse-ia`, {}, { timeout: 35000 }),
    sendEmail: (id) => api.post(`/rapports/${id}/send-email`),
    delete: (id) => api.delete(`/rapports/${id}`),
};

// ─── Honoraires ────────────────────────────────────────────
export const honorairesAPI = {
    list: (params) => api.get('/honoraires', { params }),
    getById: (id) => api.get(`/honoraires/${id}`),
    create: (data) => api.post('/honoraires', data),
    update: (id, data) => api.put(`/honoraires/${id}`, data),
    generatePDF: (id) => api.post(`/honoraires/${id}/pdf`, {}, { responseType: 'blob' }),
    exportEtat: (filters) => api.post(`/honoraires/export/etat`, filters, { responseType: 'blob' }),
    markAsPaid: (id) => api.put(`/honoraires/${id}/pay`),
    delete: (id) => api.delete(`/honoraires/${id}`),
};

// ─── Assurances ────────────────────────────────────────────
export const assurancesAPI = {
    list: (params) => api.get('/assurances', { params }),
    create: (data) => api.post('/assurances', data),
    update: (id, data) => api.put(`/assurances/${id}`, data),
    delete: (id) => api.delete(`/assurances/${id}`),
    getLogos: () => api.get('/assurances/logos'),
    uploadLogo: (formData) => api.post('/assurances/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
};

// ─── Genres ────────────────────────────────────────────────
export const genresAPI = {
    list: () => api.get('/genres'),
    create: (data) => api.post('/genres', data),
    delete: (id) => api.delete(`/genres/${id}`),
};

// ─── Marques & Modeles ─────────────────────────────────────
export const marquesAPI = {
    list: () => api.get('/marques'),
    create: (data) => api.post('/marques', data),
    uploadLogo: (id, formData) => api.post(`/marques/${id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id) => api.delete(`/marques/${id}`),
};

export const modelesAPI = {
    list: (params) => api.get('/modeles', { params }),
    getYears: (brandId, modelName) => api.get('/modeles/years', { params: { brand_id: brandId, model_name: modelName } }),
    create: (data) => api.post('/modeles', data),
    delete: (id) => api.delete(`/modeles/${id}`),
};

export const carosseriesAPI = {
    list: () => api.get('/carosseries'),
    create: (data) => api.post('/carosseries', data),
    delete: (id) => api.delete(`/carosseries/${id}`),
};

export const colorsAPI = {
    list: () => api.get('/colors'),
    create: (data) => api.post('/colors', data),
    delete: (id) => api.delete(`/colors/${id}`),
};

export const energiesAPI = {
    list: () => api.get('/energies'),
    create: (data) => api.post('/energies', data),
    delete: (id) => api.delete(`/energies/${id}`),
};

export const estadosAPI = {
    list: () => api.get('/estados'),
    create: (data) => api.post('/estados', data),
    delete: (id) => api.delete(`/estados/${id}`),
};

// ─── Pieces ────────────────────────────────────────────────
export const piecesAPI = {
    list: (params) => api.get('/pieces', { params }),
    create: (data) => api.post('/pieces', data),
    update: (id, data) => api.put(`/pieces/${id}`, data),
    delete: (id) => api.delete(`/pieces/${id}`),
};

export const pieceCategoriesAPI = {
    list: () => api.get('/piece-categories'),
    create: (name) => api.post('/piece-categories', { name }),
    delete: (id) => api.delete(`/piece-categories/${id}`),
};

// ─── Photos ────────────────────────────────────────────────
export const photosAPI = {
    upload: (formData, onProgress) => api.post('/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress,
        timeout: 0 // Disable timeout for slow upload connections
    }),
    list: (params) => api.get('/photos', { params }),
    delete: (id) => api.delete(`/photos/${id}`),
};

// ─── Params ────────────────────────────────────────────────
export const paramsAPI = {
    list: () => api.get('/params'),
    update: (data) => api.put('/params', { params: data }),
    uploadSystemFile: (formData) => api.post('/params/upload-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
};

// ─── Users ────────────────────────────────────────────────
export const usersAPI = {
    list: () => api.get('/users'),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    getActive: () => api.get('/users/active'),
    eject: (id) => api.post(`/users/${id}/eject`),
    unlock: (id) => api.post(`/users/${id}/unlock`),
    resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
};

// ─── Audit ─────────────────────────────────────────────────
export const auditAPI = {
    list: (params) => api.get('/audit', { params }),
};

// ─── Reporting ─────────────────────────────────────────────
export const reportingAPI = {
    get: (params) => api.get('/reporting', { params }),
};

export const templatesAPI = {
    list: (params) => api.get('/templates', { params }),
};

// ─── IA Extraction ──────────────────────────────────────────
export const aiAPI = {
    extractSinistreData: (formData) => api.post('/ai/analyze-sinistre', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
    }),
};

// ─── HABIB AI Agent ─────────────────────────────────────────
export const agentAPI = {
    chat:         (data) => api.post('/agent/chat', data, { timeout: 75000 }),
    getHistory:   (sessionId) => api.get('/agent/history', { params: { sessionId } }),
    clearHistory: (sessionId) => api.delete('/agent/history', { data: { sessionId } }),
};

export default api;
